/**
 * Восстановление после выкатки, случившейся под открытой вкладкой.
 *
 * <p><b>Что ломается.</b> Webpack режет бандл на куски с хешем в имени
 * (`697.440d8ee6.js`). После деплоя на сервере лежат куски с <b>другими</b> хешами, а
 * вкладка, открытая до выкатки, продолжает просить старые — их больше нет, и первая же
 * ленивая подгрузка падает с `ChunkLoadError: Loading chunk 697 failed`. Именно эти ошибки
 * и приехали в отчёты 11.09.2026 (issue 16 и 17).
 *
 * <p><b>Почему это относится к звонкам.</b> Экран звонка и движок LiveKit подгружаются
 * ленивыми кусками. Для человека с открытой со вчера вкладкой это выглядит как «нажал
 * ответить — ничего не произошло»: кусок не загрузился, экрана нет, звонок звонит в пустоту.
 *
 * <p><b>Лечение.</b> Единственное честное — взять новую версию страницы, то есть
 * перезагрузиться. Один раз: если и после перезагрузки кусок не грузится, дело не в
 * устаревшем хеше (сеть, блокировщик, битый деплой), и бесконечный цикл перезагрузок сделает
 * только хуже. Отметку о попытке держим в `sessionStorage` — она живёт ровно эту вкладку,
 * не переживает закрытия и не мешает следующей выкатке.
 *
 * <p>Разговор перезагрузка прервёт, и это осознанный размен: без куска экрана звонка всё
 * равно нет, а вкладка со сломанным ленивым импортом не чинится ничем.
 */

const RELOAD_MARK_KEY = 'vrm:chunk-reload-at';
/** Перезагружаться не чаще раза в минуту: быстрее — это уже цикл, а не восстановление. */
const RELOAD_COOLDOWN_MS = 60_000;

/**
 * Сбой подгрузки куска бандла — или обычная ошибка приложения.
 *
 * <p>Экспортируется ради теста: перезагрузка страницы по ошибке, которую она не лечит,
 * оборвёт разговор на ровном месте, поэтому решать это должна проверяемая функция, а не
 * условие внутри обработчика события.
 */
export function isStaleChunkError(value: unknown): boolean {
  if (!value) return false;
  const name = (value as { name?: unknown }).name;
  if (name === 'ChunkLoadError') return true;
  const message = String((value as { message?: unknown }).message ?? value);
  return (
    /Loading chunk [^\s]+ failed/i.test(message) ||
    /Loading CSS chunk [^\s]+ failed/i.test(message) ||
    // Динамический импорт в Safari и Firefox падает своими формулировками.
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

function reloadedRecently(): boolean {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_MARK_KEY);
    const at = raw ? Number(raw) : 0;
    return Number.isFinite(at) && at > 0 && Date.now() - at < RELOAD_COOLDOWN_MS;
  } catch {
    // Приватный режим и запрет на хранилище: считаем, что не перезагружались. Хуже, чем
    // отметка, но лучше, чем не починить вкладку вовсе.
    return false;
  }
}

function markReloaded(): void {
  try {
    window.sessionStorage.setItem(RELOAD_MARK_KEY, String(Date.now()));
  } catch {
    /* см. reloadedRecently */
  }
}

function handle(value: unknown): void {
  if (!isStaleChunkError(value) || reloadedRecently()) {
    return;
  }
  markReloaded();
  console.warn('[vrm] кусок бандла устарел после выкатки — перезагружаем страницу');
  window.location.reload();
}

/** Подписаться на оба канала, которыми до нас доходит упавшая подгрузка куска. */
export function installStaleChunkRecovery(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.addEventListener('error', (event) => {
    handle(event.error ?? event.message);
  });
  window.addEventListener('unhandledrejection', (event) => {
    handle((event as PromiseRejectionEvent).reason);
  });
}
